import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  signInWithEmail,
  signUpWithEmail,
  resetPassword as resetPasswordService,
  updatePassword as updatePasswordService,
  signOut,
  getSession,
} from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // Check for existing session on mount
    checkSession();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);

        // Handle PASSWORD_RECOVERY event - user clicked reset link from email
        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery mode activated');
          setIsPasswordRecovery(true);
        }

        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.email,
            avatar: session.user.user_metadata?.avatar_url,
          });
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  async function checkSession() {
    try {
      const session = await getSession();
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.email,
          avatar: session.user.user_metadata?.avatar_url,
        });
      }
    } catch (err) {
      console.error('Error checking session:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loginWithEmail(email, password) {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmail(email, password);
    } catch (err) {
      console.error('Error logging in with email:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }

  async function signUp(email, password) {
    try {
      setError(null);
      setLoading(true);
      const result = await signUpWithEmail(email, password);
      setLoading(false);
      return result;
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }

  async function resetPassword(email) {
    try {
      setError(null);
      setLoading(true);
      await resetPasswordService(email);
      setLoading(false);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }

  async function updatePassword(newPassword) {
    try {
      setError(null);
      setLoading(true);
      await updatePasswordService(newPassword);
      // Clear password recovery mode after successful update
      setIsPasswordRecovery(false);
      setLoading(false);
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }

  // Clear password recovery mode (used when user navigates away)
  function clearPasswordRecovery() {
    setIsPasswordRecovery(false);
  }

  async function logout() {
    try {
      setError(null);
      await signOut();
      setUser(null);
    } catch (err) {
      console.error('Error logging out:', err);
      setError(err.message);
    }
  }

  const value = {
    user,
    loading,
    error,
    isPasswordRecovery,
    loginWithEmail,
    signUp,
    resetPassword,
    updatePassword,
    clearPasswordRecovery,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
